// Calls of the Penyedia work tools (/paketwork/:id/..., apps/api module
// paketwork), through the typed hc<AppType> client. Response types come
// from the routes; downloads are plain URLs for a normal navigation (the
// same pattern as dbExportUrl).
import type { InferRequestType, InferResponseType } from "hono/client";
import { send } from "../../api";
import { API_URL, api } from "../../client";

const work = api.paketwork[":id"];

type MatrixRes = InferResponseType<(typeof work)["requirements"]["$get"], 200>;
type RabRes = InferResponseType<(typeof work)["rab"]["$get"], 200>;
type QualityRes = InferResponseType<(typeof work)["quality"]["$get"], 200>;
type ContractRes = InferResponseType<(typeof work)["contract"]["$get"], 200>;

export type RequirementMatrix = MatrixRes["matrix"];
export type Requirement = RequirementMatrix["items"][number];
export type RequirementCategory = Requirement["category"];
export type RequirementStatus = Requirement["status"];
export type RequirementsSave = InferRequestType<(typeof work)["requirements"]["$put"]>["json"];
export type ExtractInput = InferRequestType<(typeof work)["requirements"]["extract"]["$post"]>["json"];
export type Rab = RabRes["rab"];
export type RabSave = InferRequestType<(typeof work)["rab"]["$put"]>["json"];
export type Quality = QualityRes["quality"];
export type QualitySave = InferRequestType<(typeof work)["quality"]["$put"]>["json"];
export type ContractView = ContractRes["contract"];
export type ContractEntry = NonNullable<ContractView["current"]>;
export type ContractInput = InferRequestType<(typeof work)["contract"]["$post"]>["json"];

export async function getRequirements(id: string): Promise<RequirementMatrix> {
  return (await send<MatrixRes>(() => work.requirements.$get({ param: { id } }))).matrix;
}

export async function saveRequirements(id: string, json: RequirementsSave): Promise<RequirementMatrix> {
  return (await send<MatrixRes>(() => work.requirements.$put({ param: { id }, json }))).matrix;
}

/** Replaces the matrix with what Pluto finds in the text or the project file. */
export async function extractRequirements(id: string, json: ExtractInput): Promise<RequirementMatrix> {
  return (await send<MatrixRes>(() => work.requirements.extract.$post({ param: { id }, json }))).matrix;
}

export async function getRab(id: string): Promise<Rab> {
  return (await send<RabRes>(() => work.rab.$get({ param: { id } }))).rab;
}

export async function saveRab(id: string, json: RabSave): Promise<Rab> {
  return (await send<RabRes>(() => work.rab.$put({ param: { id }, json }))).rab;
}

export function rabExportUrl(id: string, format: "docx" | "xlsx"): string {
  return `${API_URL}/paketwork/${encodeURIComponent(id)}/rab/export?format=${format}`;
}

export async function getQuality(id: string): Promise<Quality> {
  return (await send<QualityRes>(() => work.quality.$get({ param: { id } }))).quality;
}

export async function saveQuality(id: string, json: QualitySave): Promise<Quality> {
  return (await send<QualityRes>(() => work.quality.$put({ param: { id }, json }))).quality;
}

export function qualityReportUrl(id: string): string {
  return `${API_URL}/paketwork/${encodeURIComponent(id)}/quality/report`;
}

export async function getContract(id: string): Promise<ContractView> {
  return (await send<ContractRes>(() => work.contract.$get({ param: { id } }))).contract;
}

/** Appends one monitoring entry; the answer is the whole history, newest first. */
export async function recordContract(id: string, json: ContractInput): Promise<ContractView> {
  return (await send<ContractRes>(() => work.contract.$post({ param: { id }, json }))).contract;
}
